import React, { FC, useEffect, useState } from 'react';
import BlankPage from '../../Components/Layout/BlankPage';
import Api from '../../Vendor/Api';

interface QuotaData {
    monthly_token_limit: number | null;
    current_month_tokens_used: number;
    total_month_tokens_used?: number;
    remaining_tokens: number | null;
    percentage_used: number;
    is_quota_exceeded: boolean;
    warning_level: string;
    threshold_crossed: number;
    estimated_cost_eur?: number | null;
    estimated_cost_usd?: number | null;
    currency?: string;
}

const UsagePage: FC = () => {
    const [quota, setQuota] = useState<QuotaData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        Api.get('/ai/quota')
            .then((res) => {
                setQuota(res.data);
            })
            .catch((err) => {
                console.error('Failed to load AI quota stats', err);
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    if (loading) {
        return (
            <BlankPage>
                <div className="d-flex justify-content-center align-items-center py-5">
                    <div className="spinner-border text-secondary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </BlankPage>
        );
    }

    if (!quota) return <BlankPage><></></BlankPage>;

    const isExceeded = quota.is_quota_exceeded;
    const pct = quota.percentage_used;
    const warningLevel = quota.warning_level;

    return (
        <BlankPage>
            <div className="mb-4">
                {isExceeded ? (
                    <div className="alert alert-danger d-flex align-items-center mb-4 rounded-3 shadow-sm border-0" role="alert">
                        <i className="fa-solid fa-triangle-exclamation fs-4 me-3"></i>
                        <div>
                            <h6 className="alert-heading mb-1 fw-bold">Warning: Monthly AI token quota reached!</h6>
                            <span className="small">
                                Your organization has used <strong>100%</strong> of its monthly token allowance (0 remaining tokens). AI vision autotagging is currently paused. Please contact your organization administrator to upgrade the monthly limit.
                            </span>
                        </div>
                    </div>
                ) : warningLevel === 'warning' ? (
                    <div className="alert alert-warning d-flex align-items-center mb-4 rounded-3 shadow-sm border-0" role="alert">
                        <i className="fa-solid fa-circle-exclamation fs-4 me-3"></i>
                        <div>
                            <h6 className="alert-heading mb-1 fw-bold">Notice: Approaching monthly token quota</h6>
                            <span className="small">
                                You have used <strong>{pct}%</strong> of your monthly AI token allowance.
                            </span>
                        </div>
                    </div>
                ) : null}

                <div className="row g-3 mb-4">
                    <div className="col-md-6">
                        <div className="p-3 bg-white rounded-3 border shadow-sm h-100">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="fw-semibold text-muted small">Current cycle token usage</span>
                                <span className="badge bg-light text-muted border px-2 py-1 font-monospace fw-semibold fs-6">
                                    {(quota.current_month_tokens_used ?? 0).toLocaleString()} tokens
                                </span>
                            </div>
                            <div className="progress mb-2" style={{ height: '10px', borderRadius: '6px' }}>
                                <div
                                    className={`progress-bar ${isExceeded ? 'bg-danger' : pct >= 80 ? 'bg-warning' : 'bg-success'}`}
                                    role="progressbar"
                                    style={{ width: `${pct}%` }}
                                    aria-valuenow={pct}
                                    aria-valuemin={0}
                                    aria-valuemax={100}
                                ></div>
                            </div>
                            <div className="d-flex justify-content-between small text-muted">
                                <span>Used: <strong>{pct}%</strong></span>
                                <span>Monthly limit: <strong>{quota.monthly_token_limit ? quota.monthly_token_limit.toLocaleString() : 'Unlimited'}</strong></span>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="p-3 bg-white rounded-3 border shadow-sm h-100 d-flex flex-column justify-content-between">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="fw-semibold text-muted small">Remaining token allowance</span>
                                <span className="badge bg-light text-muted border px-2 py-1 font-monospace fw-semibold fs-6">
                                    {quota.remaining_tokens !== null ? quota.remaining_tokens.toLocaleString() : 'Unlimited'}
                                </span>
                            </div>
                            {quota.total_month_tokens_used !== undefined && quota.total_month_tokens_used > 0 && (
                                <div className="d-flex justify-content-between align-items-center small text-muted mt-2 pt-2 border-top">
                                    <span>Total tokens used this month:</span>
                                    <span className="fw-bold text-dark font-monospace">{quota.total_month_tokens_used.toLocaleString()} tokens</span>
                                </div>
                            )}
                            {quota.estimated_cost_eur !== undefined && quota.estimated_cost_eur !== null ? (
                                <div className="d-flex justify-content-between align-items-center small text-muted mt-2 pt-2 border-top">
                                    <span>Estimated usage cost:</span>
                                    <span className="fw-bold text-dark">{quota.estimated_cost_eur.toFixed(2)} €</span>
                                </div>
                            ) : quota.estimated_cost_usd !== undefined && quota.estimated_cost_usd !== null ? (
                                <div className="d-flex justify-content-between align-items-center small text-muted mt-2 pt-2 border-top">
                                    <span>Estimated usage cost:</span>
                                    <span className="fw-bold text-dark">${quota.estimated_cost_usd.toFixed(2)}</span>
                                </div>
                            ) : null}
                            <div className="d-flex justify-content-between align-items-center small text-muted mt-2 pt-2 border-top">
                                <span>AI service status:</span>
                                {isExceeded ? (
                                    <span className="badge bg-danger text-white">Paused (Quota exceeded)</span>
                                ) : (
                                    <span className="badge bg-success text-white">Active</span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </BlankPage>
    );
};

export default UsagePage;
